
import { useEffect, useMemo, useState } from 'react'
import { formatMetricValue } from "../../lib/reportNew/formatNew"
import { HEIGHT_PX } from '../../lib/reportNew/widgetsNew'
import { AreaChartGraph, BarChartGraph, LineChartGraph } from './cartesian-chartsNew'
import { PieChartGraph } from './pie-chart-graphNew'
import { RankingTable } from './ranking-tableNew'
import { IconButton } from '../ui-kitNew/buttonNew'
import { ChevronLeftIcon, ChevronRightIcon } from '../ui-kitNew/iconsNew'

/** Nº de categorías por página según el tipo de gráfico y el ancho del widget. */
function pageSizeFor(chartType, span) {
  if (['pie', 'donut', 'ranking'].includes(chartType)) return 0
  if (chartType === 'barHorizontal') return span >= 2 ? 12 : 8
  return span >= 2 ? 12 : 6
}

/**
 * Renderiza cualquier tipo de gráfico a partir de datos ya agregados y,
 * cuando hay demasiadas categorías, las pagina con controles laterales
 * (mismo patrón que el carrusel de barras de la aplicación actual).
 */
export function ChartRenderer({ widget, result }) {
  const { chartType, span = 1, height = 'md', showLegend = true } = widget
  const pageSize = pageSizeFor(chartType, span)
  const [page, setPage] = useState(0)

  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(result.data.length / pageSize)) : 1

  useEffect(() => {
    if (page > totalPages - 1) setPage(0)
  }, [page, totalPages])

  const pageData = useMemo(() => {
    if (pageSize <= 0) return result.data
    const start = page * pageSize
    return result.data.slice(start, start + pageSize)
  }, [result.data, page, pageSize])

  if (!result.data.length) {
    return (
      <div
        style={{ height: HEIGHT_PX[height] }}
        className="flex items-center justify-center text-[13px] text-muted-foreground"
      >
        Ningún registro cumple los filtros aplicados
      </div>
    )
  }

  if (chartType === 'ranking') {
    return <RankingTable result={result} chartType={chartType} />
  }

  const common = { data: pageData, series: result.series, format: result.format, metricMap: result.metricMap, showLegend, chartType }

  return (
    <div className="relative">
      {totalPages > 1 && (
        <div className="mb-1 flex items-center justify-between px-1">
          <span className="text-[12px] text-muted-foreground">
            {`Página ${page + 1} de ${totalPages}`}
          </span>
          <span className="flex items-center gap-0.5">
            <IconButton
              label="Categorías anteriores"
              disabled={page === 0}
              onClick={() => setPage((prev) => Math.max(0, prev - 1))}
            >
              <ChevronLeftIcon size={16} />
            </IconButton>
            <IconButton
              label="Categorías siguientes"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
            >
              <ChevronRightIcon size={16} />
            </IconButton>
          </span>
        </div>
      )}

      <div style={{ height: HEIGHT_PX[height] }}>
        {chartType === 'bar' && <BarChartGraph {...common} colorByPoint />}
        {chartType === 'barStacked' && <BarChartGraph {...common} stacked />}
        {chartType === 'barHorizontal' && <BarChartGraph {...common} horizontal colorByPoint />}
        {chartType === 'line' && <LineChartGraph {...common} />}
        {chartType === 'area' && <AreaChartGraph {...common} />}
        {chartType === 'donut' && <PieChartGraph {...common} donut />}
        {chartType === 'pie' && <PieChartGraph {...common} donut={false} />}
      </div>

      {result.series.length === 1 && chartType !== 'donut' && chartType !== 'pie' && (
        <p className="mt-1 px-1 text-[12px] text-muted-foreground">
          {`${result.label} · Total visible: ${formatMetricValue(
            pageData.reduce((acc, item) => acc + item.__total, 0),
            { ...result.format, compact: true }
          )}`}
        </p>
      )}
    </div>
  )
}
